import PropTypes from 'prop-types';
import { useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';

// @mui
import { styled } from '@mui/material/styles';
import { Box, Stack, Drawer, Typography } from '@mui/material';
import { FaCog, FaRegQuestionCircle } from 'react-icons/fa';

import { DashboardModeContext } from '../../../contexts/DashboardModeContext';

// hooks
import useResponsive from '../../../hooks/useResponsive';
import useAuth from '../../../hooks/useAuth';

// config
import { NAVBAR, DASHBOARD_VERSION } from '../../../config';

// components
import Logo from '../../../components/Logo';
import Scrollbar from '../../../components/Scrollbar';
import { NavSectionVertical } from '../../../components/nav-section';
import { NavListRoot } from '../../../components/nav-section/vertical/NavList';

import navConfig from './NavConfig';
import groupNavConfig from './GroupNavConfig';
import NavbarAccount from './NavbarAccount';
import NavbarCompanySelect from './NavbarCompanySelect';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {

  const { pathname } = useLocation();
  const { user } = useAuth();
  const isDesktop = useResponsive('up', 'lg');
  const groupContext = useContext(DashboardModeContext);

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack
        spacing={2}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Logo inverted />
        </Stack>

        <NavbarAccount />
        <NavbarCompanySelect />
      </Stack>

      { /* Navbar will only display if onboarding is either not required or is already complete */ }
      { (user?.company?.onboarding_required === false || user?.company?.onboarding_complete === true) && ( 
        <>
          { groupContext.isGroupMode ? <NavSectionVertical navConfig={groupNavConfig(user)} /> : <NavSectionVertical navConfig={navConfig(user)} /> }
          <Box sx={{ flexGrow: 1 }} />            
          <Box sx={{ p: 2 }} mt={2}>
            { groupContext.isGroupMode === false && ( 
              <Box sx={{ p: 0 }} mb={2}>          
                  <NavListRoot list={{ title: 'Company Settings', icon: <FaCog style={{ width: 15, height: 15 }} />, path: '/company/settings' }} />
                  <NavListRoot list={{ title: 'Help & Support', icon: <FaRegQuestionCircle style={{ width: 15, height: 15 }} />, path: '/tickets' }} />
              </Box>
            )}
            <Typography variant="body2" component="div" sx={{ fontSize: '12px' }}><strong>Client Dashboard {DASHBOARD_VERSION}</strong></Typography>
            { (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') ? 
              ( <Typography variant="body2" component="div" sx={{ fontSize: '11px' }}>Development Build</Typography> ) : 
              ( <Typography variant="body2" component="div" sx={{ fontSize: '11px' }}>Production</Typography> ) 
            }
          </Box>
        </>
      )}    

    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: 280,
        },
      }}
    >
      {!isDesktop && (
        <Drawer open={isOpenSidebar} onClose={onCloseSidebar} PaperProps={{ sx: { 
            width: NAVBAR.DASHBOARD_WIDTH, 
            bgcolor: 'primaryNavy.main', 
            color: 'common.white', 
          } }}>
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRightStyle: 'dashed',
              bgcolor: 'primaryNavy.main', 
              color: 'common.white', 
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
